import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw } from "lucide-react";
import { FIX_GUIDES } from "@/lib/fixGuides";
import { HowToFix } from "@/components/nds/HowToFix";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";
import { ListTools, sortRows } from "@/components/nds/ListTools";
import { BulkObsolete, ClearedRow, MarkObsolete } from "@/components/nds/ProblemObsolete";

const GET = { name: (p) => p.resident_name || p.title, date: (p) => p.since };
const GET_CLEARED = { name: (c) => c.snapshot?.resident_name || c.snapshot?.title, date: (c) => c.cleared_at };

const API = `${process.env.REACT_APP_BACKEND_URL}/api/problems`;
const when = (v) => (v ? String(v).slice(0, 10) : "");

/** Every open flag across the platform; each row has its fix steps. Rows disappear on their own once the record is fixed. */
export default function OpsProblemsContent() {
  const [d, setD] = useState(null);
  const [code, setCode] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("urgent");
  const [view, setView] = useState("open");
  const [picked, setPicked] = useState([]);
  const load = () => axios.get(API, { headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` } }).then((r) => setD(r.data)).catch(() => setD({ problems: [], counts: {}, total: 0, error: true }));
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const evs = ["fin:paycheck-logged", "problems:changed"];
    evs.forEach((e) => window.addEventListener(e, load));
    return () => { clearInterval(t); evs.forEach((e) => window.removeEventListener(e, load)); };
  }, []);
  useEffect(() => {
    if (!d || !code) return;
    const list = view === "cleared" ? (d.cleared || []) : d.problems;
    if (!list.some((x) => x.code === code)) setCode("");
  }, [d, view, code]);
  if (!d) return <div className="p-6 flex items-center gap-2 text-nex-muted text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const changed = () => { load(); window.dispatchEvent(new Event("problems:changed")); };
  const dropOpen = (...pids) => setD((x) => {
    const gone = x.problems.filter((y) => pids.includes(y.id));
    const counts = { ...x.counts };
    gone.forEach((p) => { counts[p.code] = (counts[p.code] || 1) - 1; });
    return { ...x, problems: x.problems.filter((y) => !pids.includes(y.id)), total: x.total - gone.length, counts };
  });
  const cleared = d.cleared || [];
  const showCleared = view === "cleared" && d.can_obsolete;
  const clearedCounts = cleared.reduce((a, c) => ({ ...a, [c.code]: (a[c.code] || 0) + 1 }), {});
  const rows = sortRows(d.problems.filter((p) => !code || p.code === code), GET, sort, q);
  const crows = sortRows(cleared.filter((c) => !code || c.code === code), GET_CLEARED, sort, q);
  const counts = showCleared ? clearedCounts : d.counts;
  const sel = picked.filter((id) => rows.some((p) => p.id === id));
  const allOn = rows.length > 0 && rows.every((p) => sel.includes(p.id));
  const toggle = (id) => setPicked((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const chip = (k, label, n) => (
    <button key={k || "all"} type="button" onClick={() => setCode(k)} data-testid={`problems-filter-${k || "all"}`} aria-pressed={code === k}
      className={`px-2.5 py-1 rounded-full border font-mono text-[9px] tracking-[0.16em] uppercase transition-colors ${code === k ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>{label} · {n}</button>
  );
  return (
    <div data-testid="problems-inbox" className="p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span data-testid="problems-total" className="text-2xl font-semibold text-nex-silver">{d.total} open</span>
        <span className="text-xs text-nex-muted">Problems clear themselves once the record is fixed.</span>
        <button type="button" onClick={load} data-testid="problems-refresh" aria-label="Refresh" className="ml-auto text-nex-muted hover:text-nex-gold"><RefreshCw className="h-4 w-4" /></button>
      </div>
      {d.error && <p data-testid="problems-error" className="text-sm text-red-300">Couldn't load problems. Press refresh to try again.</p>}
      <ListTools prefix="problems" q={q} setQ={setQ} sort={sort} setSort={setSort} get={GET} />
      <div className="flex flex-wrap gap-1.5">
        {showCleared && <button type="button" onClick={() => { setView("open"); setCode(""); }} data-testid="problems-back-open"
          className="px-2.5 py-1 rounded-full border border-white/10 text-nex-muted hover:text-nex-silver font-mono text-[9px] tracking-[0.16em] uppercase">← Open problems · {d.total}</button>}
        {chip("", showCleared ? "All cleared" : "All", showCleared ? cleared.length : d.total)}
        {Object.entries(counts).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).map(([k, n]) => chip(k, FIX_GUIDES[k]?.title || k, n))}
        {d.can_obsolete && (
          <button type="button" onClick={() => { setView("cleared"); setCode(""); }} data-testid="problems-filter-cleared" aria-pressed={showCleared}
            className={`px-2.5 py-1 rounded-full border font-mono text-[9px] tracking-[0.16em] uppercase transition-colors ${showCleared ? "border-sky-400 text-sky-300 bg-sky-500/10" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>Cleared as obsolete · {cleared.length}</button>
        )}
      </div>
      {d.can_obsolete && !showCleared && rows.length > 0 && (
        <label className="inline-flex items-center gap-2 text-[11px] text-nex-muted" data-testid="problems-select-all-label">
          <input type="checkbox" checked={allOn} onChange={() => setPicked(allOn ? [] : rows.map((p) => p.id))} data-testid="problems-select-all" className="accent-[#d4a017] h-3.5 w-3.5" />
          Select all on this filter ({rows.length})
        </label>
      )}
      {d.can_obsolete && !showCleared && <BulkObsolete selected={sel} problems={rows} onRefresh={load} onDone={(ids) => { dropOpen(...ids); setPicked([]); changed(); }} />}
      {showCleared ? (
        <ul className="border border-white/10 rounded-sm divide-y divide-white/5" data-testid="problems-cleared-list">
          {crows.map((c) => <ClearedRow key={c.key} c={c} onRestored={changed} onRefresh={load} />)}
          {!crows.length && <li data-testid="problems-cleared-empty" className="p-8 text-center text-sm text-nex-muted">Nothing cleared as obsolete.</li>}
        </ul>
      ) : (
      <ul className="border border-white/10 rounded-sm divide-y divide-white/5">
        {rows.map((p) => (
          <li key={p.id} data-testid={`problem-${p.id}`} className="px-4 py-3 space-y-1.5">
            <div className="flex flex-wrap items-baseline gap-2">
              {d.can_obsolete && <input type="checkbox" checked={sel.includes(p.id)} onChange={() => toggle(p.id)} aria-label={`Select ${p.title}`}
                data-testid={`problem-select-${p.id.replace(/[^\w-]/g, "-")}`} className="self-center accent-[#d4a017] h-3.5 w-3.5" />}
              <span className="text-sm text-amber-200 font-medium">{p.title}</span>
              {p.resident_name && <span className="text-sm text-nex-silver">· {p.resident_name}</span>}
              {p.resident_id && <OpenResidentButton residentId={p.resident_id} name={p.resident_name} testid={`open-resident-problem-${p.id}`} />}
              <span className="ml-auto font-mono text-[10px] text-nex-muted">{when(p.since)}</span>
            </div>
            {p.detail && <div className="text-xs text-nex-muted">{p.detail}</div>}
            <HowToFix code={p.code} ctx={{ ...p.ctx, problemId: p.id }} details={`${p.title} · ${p.resident_name || ""} · ${p.detail}`} testid={`problem-fix-${p.id.replace(/[^\w-]/g, "-")}`}>
              {d.can_obsolete && <MarkObsolete problem={p} testid={`problem-fix-${p.id.replace(/[^\w-]/g, "-")}`} onCleared={(pid) => { dropOpen(pid); changed(); }} onRefresh={load} />}
            </HowToFix>
          </li>
        ))}
        {!rows.length && <li data-testid="problems-empty" className="p-8 text-center text-sm text-nex-muted">Nothing open here. Nice work.</li>}
      </ul>
      )}
      {!showCleared && d.notices?.length > 0 && <div className="space-y-1" data-testid="problems-notices">
        <div className="nds-label-dim text-[9px]">Low priority · logged with warning · reason on file ({d.notices.length})</div>
        <ul className="border border-white/5 rounded-sm divide-y divide-white/5">{d.notices.map((p) => (
          <li key={p.id} data-testid={`problem-notice-${p.ctx.paycheckId}`} className="px-3 py-2 text-[11px] text-nex-muted">
            <span className="text-nex-silver">{p.resident_name}</span> · logged with warning · reason on file · {p.detail}</li>))}</ul>
      </div>}
    </div>
  );
}
